import React, { useMemo, useState } from "react";
import { Check, Plus, Lock, ArrowRight, FileText, Download, MessageCircle, Mail, Languages, Copy, QrCode, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";
import { STRINGS } from "@/lib/i18n";
import { PACKAGES, BENEFITS, T, PRESENTATION_URL, WHATSAPP_NUMBER, COMMERCIAL_EMAIL, CAR_TIER, UNIFORM_LOWER, UNIFORM_HIGHER, MERCH_LOWER, MERCH_HIGHER, PIX_KEY, buildPixPayload, resolvePackageIncludes } from "@/lib/sponsorship";

const price = (o, lang) => lang === "pt" ? `R$ ${o.brl.toLocaleString("pt-BR")}` : `US$ ${o.usd.toLocaleString("en-US")}`;

export default function Sponsorship() {
  const cart = useCart(); const { lang, setLang, pkgId, benefitIds, packageIncludes, selectPackage, clearPackage, toggleBenefit, removeBenefit, canAddBenefit, hasBenefit } = cart;
  const t = T[lang];
  const [form, setForm] = useState({ company:"", person:"", email:"", phone:"", country:"", message:"" });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPix, setShowPix] = useState(false);
  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const pkg = PACKAGES.find(p => p.id === pkgId);
  const extras = benefitIds.map(id => BENEFITS.find(b => b.id === id)).filter(Boolean);
  const total = useMemo(() => {
    const base = pkg ? (lang==="pt" ? pkg.brl : pkg.usd) : 0;
    const exSum = extras.reduce((s,b) => s + (lang==="pt"?b.brl:b.usd), 0);
    return base + exSum;
  }, [pkgId, benefitIds, lang]);
  const totalFmt = lang==="pt" ? `R$ ${total.toLocaleString("pt-BR")}` : `US$ ${total.toLocaleString("en-US")}`;

  const handleAddBenefit = (id) => {
    const chk = canAddBenefit(id);
    if (!chk.ok && chk.reason === "included") { toast.error(t.alreadyIncluded); return; }
    if (!chk.ok && chk.reason === "replaced") { toast.error(t.replacedNote); return; }
    toggleBenefit(id);
  };

  const buildSummaryText = () => {
    const lines = [];
    lines.push(`${t.company}: ${form.company}`);
    lines.push(`${t.person}: ${form.person}`);
    lines.push(`${t.email}: ${form.email}`);
    lines.push(`${t.phone}: ${form.phone}`);
    lines.push(`${t.country}: ${form.country}`);
    lines.push("");
    if (pkg) lines.push(`${lang==="pt"?"Cota":"Package"}: ${pkg.name[lang]} — ${price(pkg,lang)}`);
    if (packageIncludes.length) lines.push(`${t.packageIncludes}: ${packageIncludes.map(id=>BENEFITS.find(b=>b.id===id)?.name[lang]).filter(Boolean).join("; ")}`);
    if (extras.length) lines.push(`${t.extraBenefits}: ${extras.map(b=>`${b.name[lang]} (${price(b,lang)})`).join("; ")}`);
    lines.push(`${t.total}: ${totalFmt} (${t.cur})`);
    if (form.message) lines.push(`${t.msg}: ${form.message}`);
    const presUrl = PRESENTATION_URL[lang];
    lines.push(`${t.presLine}: ${presUrl}`);
    return lines.join("\n");
  };

  const validate = () => {
    if (!form.company.trim() || !form.person.trim() || !form.email.trim() || !form.phone.trim()) return false;
    if (!pkgId && benefitIds.length === 0) return false;
    return true;
  };

  const onWhatsApp = () => { if (!validate()) { toast.error(t.valErr); return; } window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildSummaryText())}`, "_blank"); setShowConfirm(true); };
  const onEmail = () => { if (!validate()) { toast.error(t.valErr); return; } const subject = "New Sponsorship Request - Alight Racing"; window.location.href = `mailto:${COMMERCIAL_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildSummaryText())}`; setShowConfirm(true); toast.success(t.mailtoReady); };
  const onPay = () => { if (!validate()) { toast.error(t.valErr); return; } setShowPix(true); };
  const copyText = (text) => { navigator.clipboard.writeText(text).then(() => toast.success(t.pixCopied)); };

  const benefitStatus = (id) => {
    if (packageIncludes.includes(id)) return "included";
    if (packageIncludes.includes(UNIFORM_HIGHER) && id === UNIFORM_LOWER) return "replaced";
    if (packageIncludes.includes(MERCH_HIGHER) && id === MERCH_LOWER) return "replaced";
    const carInPkg = packageIncludes.find(x => CAR_TIER[x]);
    if (carInPkg && CAR_TIER[id] && CAR_TIER[id] <= CAR_TIER[carInPkg]) return "replaced";
    if (hasBenefit(id)) return "selected";
    return "available";
  };

  return (
    <>
      <Seo title={STRINGS[lang].seo_shop_title} description={STRINGS[lang].seo_shop_desc} />
      {/* HERO */}
      <section className="pt-12 pb-6 carbon-tex">
        <div className="container-ar flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-3">{t.shop}</div>
            <h1 className="h1-hero text-4xl md:text-6xl">{lang==="pt"?"Construa sua ":"Build Your "}<span className="text-[#e77300]">{lang==="pt"?"Cota":"Package"}</span></h1>
          </div>
          <div className="flex items-center gap-2 tele-card p-1" data-testid="lang-toggle">
            <Languages className="w-4 h-4 text-[#e77300] ml-2" />
            {["en","pt"].map(l => (
              <button key={l} onClick={()=>setLang(l)} data-testid={`lang-${l}`} className={`px-3 py-1 font-mono-alt text-xs tracking-widest uppercase ${lang===l?"bg-[#e77300] text-white":"text-white/60 hover:text-white"}`}>{l==="en"?"EN · USD":"PT · BRL"}</button>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="py-12 border-t border-[#022a67]" id="packages">
        <div className="container-ar">
          <h2 className="h2-section text-3xl md:text-4xl mb-8">{t.packages}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {PACKAGES.map((p, idx) => {
              const isSel = pkgId === p.id;
              const includes = isSel ? packageIncludes : resolvePackageIncludes(p.id);
              return (
                <div key={p.id} data-testid={`pkg-card-${p.id}`} className={`tele-card p-5 flex flex-col relative ${isSel?"border-[#e77300]":""} ${idx>=3?"border-[#fdb429]/40":""}`}>
                  {idx===4 && <div className="absolute -top-3 left-4 bg-[#fdb429] text-[#020815] px-3 py-1 text-[10px] font-display uppercase tracking-widest font-bold">PREMIUM</div>}
                  <div className="eyebrow mb-2">{lang==="pt"?"Cota":"Package"} · {String(idx+1).padStart(2,"0")}</div>
                  <h3 className="font-display text-2xl uppercase mb-1">{p.name[lang]}</h3>
                  <div className={`font-display text-2xl mb-3 ${idx===4?"text-[#fdb429]":"text-[#e77300]"}`}>{price(p, lang)}</div>
                  <p className="text-xs text-[#8ba3cc] mb-4">{p.desc[lang]}</p>
                  <div className="text-[10px] font-mono-alt uppercase tracking-widest text-[#005fca] mb-2">{t.packageIncludes}</div>
                  <ul className="space-y-1 mb-4 flex-1">
                    {includes.map(bid => { const b = BENEFITS.find(x=>x.id===bid); return b && <li key={bid} className="flex items-start gap-2 text-xs text-white/80"><Check className="w-3 h-3 text-[#e77300] mt-0.5 shrink-0" />{b.name[lang]}</li>; })}
                    {p.extras && <li className="flex items-start gap-2 text-xs text-[#fdb429]"><Check className="w-3 h-3 text-[#fdb429] mt-0.5 shrink-0" />{p.extras[lang]}</li>}
                  </ul>
                  <button data-testid={`pkg-select-${p.id}`} onClick={() => isSel ? clearPackage() : selectPackage(p.id)} className={`btn-racing ${isSel?"secondary":"primary"} w-full`}>
                    <span>{isSel ? t.selected : t.select}</span>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            <a href="#custom" className="btn-racing secondary" data-testid="customize-btn"><span>{t.customBtn} <ArrowRight className="w-4 h-4" /></span></a>
          </div>
        </div>
      </section>

      {/* PRESENTATION */}
      <section className="py-12 border-t border-[#022a67] bg-[#022a67]/10">
        <div className="container-ar tele-card p-8" data-testid="presentation-section">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 border border-[#e77300] flex items-center justify-center shrink-0"><FileText className="w-6 h-6 text-[#e77300]" /></div>
            <div className="flex-1 min-w-[240px]">
              <div className="eyebrow mb-2">PDF</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mb-2">{t.presTitle}</h3>
              <p className="text-sm text-[#8ba3cc] mb-4">{t.presCopy}</p>
              <div className="flex flex-wrap gap-3">
                <a href={PRESENTATION_URL[lang]} target="_blank" rel="noreferrer" className="btn-racing primary" data-testid="view-presentation"><span>{t.viewPres} <ArrowRight className="w-4 h-4" /></span></a>
                <a href={PRESENTATION_URL[lang]} download className="btn-racing secondary" data-testid="download-presentation"><span>{t.dlPres} <Download className="w-4 h-4" /></span></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMIZE BENEFITS */}
      <section className="py-12 border-t border-[#022a67]" id="custom">
        <div className="container-ar">
          <div className="eyebrow mb-2">À la carte</div>
          <h2 className="h2-section text-3xl md:text-4xl mb-8">{t.customTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {BENEFITS.map(b => {
              const status = benefitStatus(b.id);
              const disabled = status === "included" || status === "replaced";
              return (
                <div key={b.id} data-testid={`benefit-${b.id}`} className={`tele-card overflow-hidden flex flex-col ${status==="selected"?"border-[#e77300]":""} ${disabled?"opacity-60":""}`}>
                  <div className="relative aspect-[4/3] bg-[#020815] border-b border-[#005fca]/30 overflow-hidden">
                    <img src={b.img} alt={b.name[lang]} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <span className="absolute top-2 left-2 w-3 h-3 border-t border-l border-[#e77300]" />
                    <span className="absolute top-2 right-2 w-3 h-3 border-t border-r border-[#e77300]" />
                    <span className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-[#e77300]" />
                    <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-[#e77300]" />
                    {status === "included" && <span className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#005fca] px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-widest">{t.included}</span>}
                    {status === "replaced" && <span className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#8ba3cc] text-[#020815] px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-widest">↑</span>}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="font-display uppercase text-sm mb-1">{b.name[lang]}</div>
                    <div className="text-[#e77300] font-display text-lg mb-2">{price(b,lang)}</div>
                    <p className="text-xs text-[#8ba3cc] mb-3 flex-1 line-clamp-4">{b.desc[lang]}</p>
                    {status === "included" ? <div className="text-[10px] font-mono-alt uppercase tracking-widest text-[#005fca]">{t.includedTag}</div>
                    : status === "replaced" ? <div className="text-[10px] font-mono-alt uppercase tracking-widest text-[#8ba3cc]">{t.replacedTag}</div>
                    : <button data-testid={`benefit-add-${b.id}`} onClick={()=>handleAddBenefit(b.id)} className={`btn-racing ${status==="selected"?"secondary":"primary"} w-full text-xs`}>
                        <span>{status==="selected"?<><Check className="w-3 h-3"/> {t.selected}</>:<><Plus className="w-3 h-3"/> {t.add}</>}</span>
                      </button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINALIZE */}
      <section className="py-16 border-t border-[#022a67] bg-[#022a67]/10" id="finalize">
        <div className="container-ar grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-5 tele-card p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
            <div className="eyebrow mb-2">{t.cartTitle}</div>
            {!pkg && extras.length===0 && <p className="text-sm text-[#8ba3cc] py-6">{t.cartEmpty}</p>}
            {pkg && (
              <div className="mb-4 pb-4 border-b border-[#022a67]">
                <div className="flex items-center justify-between"><div className="font-display uppercase text-lg">{pkg.name[lang]}</div><div className="text-[#e77300] font-display">{price(pkg,lang)}</div></div>
                <button onClick={clearPackage} className="text-[10px] font-mono-alt uppercase tracking-widest text-white/60 hover:text-[#e77300] mt-1">{t.change} / {t.remove}</button>
                {packageIncludes.length>0 && <ul className="mt-2 space-y-0.5">{packageIncludes.map(id=>{const b=BENEFITS.find(x=>x.id===id);return b&&<li key={id} className="text-[11px] text-[#8ba3cc]">· {b.name[lang]}</li>;})}</ul>}
                {pkg.extras && <div className="mt-1 text-[11px] text-[#fdb429]">· {pkg.extras[lang]}</div>}
              </div>
            )}
            {extras.length>0 && (
              <div className="mb-4">
                <div className="eyebrow mb-2">{t.extraBenefits}</div>
                <ul className="space-y-2">{extras.map(b => <li key={b.id} className="flex justify-between items-center text-sm"><span className="text-white/90 truncate mr-2">{b.name[lang]}</span><span className="flex items-center gap-2 shrink-0"><span className="text-[#e77300]">{price(b,lang)}</span><button onClick={()=>removeBenefit(b.id)} className="text-[10px] text-white/50 hover:text-[#e77300] font-mono-alt tracking-widest uppercase">{t.remove}</button></span></li>)}</ul>
              </div>
            )}
            <div className="border-t border-[#022a67] pt-3 flex justify-between items-baseline"><span className="eyebrow">{t.total} ({t.cur})</span><span className="font-display text-3xl text-[#e77300]" data-testid="cart-total">{totalFmt}</span></div>
          </aside>

          <div className="lg:col-span-7 tele-card p-6" data-testid="finalize-form">
            <div className="eyebrow mb-2">{t.finalize}</div>
            <h2 className="h2-section text-2xl md:text-3xl mb-5">{t.finalTitle}</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <div><label className="eyebrow block mb-1">{t.company} *</label><input className="ar-input" value={form.company} onChange={setF("company")} data-testid="fin-company" /></div>
              <div><label className="eyebrow block mb-1">{t.person} *</label><input className="ar-input" value={form.person} onChange={setF("person")} data-testid="fin-person" /></div>
              <div><label className="eyebrow block mb-1">{t.email} *</label><input type="email" className="ar-input" value={form.email} onChange={setF("email")} data-testid="fin-email" /></div>
              <div><label className="eyebrow block mb-1">{t.phone} *</label><input className="ar-input" value={form.phone} onChange={setF("phone")} data-testid="fin-phone" /></div>
              <div className="md:col-span-2"><label className="eyebrow block mb-1">{t.country}</label><input className="ar-input" value={form.country} onChange={setF("country")} data-testid="fin-country" /></div>
              <div className="md:col-span-2"><label className="eyebrow block mb-1">{t.msg}</label><textarea rows={3} className="ar-input" value={form.message} onChange={setF("message")} data-testid="fin-message" /></div>
            </div>
            <div className="mt-4 text-sm text-[#8ba3cc]">{t.total}: <span className="text-[#e77300] font-display text-xl ml-2">{totalFmt}</span></div>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              {lang === "pt" && <button onClick={onPay} className="btn-racing primary" data-testid="pay-now"><span>{t.payNow} <QrCode className="w-4 h-4"/></span></button>}
              <a onClick={onWhatsApp} className="btn-racing secondary cursor-pointer" data-testid="pay-whatsapp"><span>{t.wa} <MessageCircle className="w-4 h-4"/></span></a>
              <a onClick={onEmail} className="btn-racing secondary cursor-pointer" data-testid="pay-email"><span>{t.em} <Mail className="w-4 h-4"/></span></a>
            </div>
            {showPix && lang === "pt" && (() => { const pixPayload = buildPixPayload(total); return (
              <div className="mt-5 p-5 border border-[#fdb429]/50 bg-[#fdb429]/5 relative" data-testid="pix-panel">
                <button onClick={()=>setShowPix(false)} className="absolute top-3 right-3 text-white/50 hover:text-white" data-testid="pix-close" aria-label={t.pixClose}><X className="w-4 h-4"/></button>
                <div className="eyebrow mb-1 text-[#fdb429]">{t.pixTitle}</div>
                <p className="text-sm text-[#8ba3cc] mb-4">{t.pixScan}</p>
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  <div className="bg-white p-3 shrink-0" data-testid="pix-qr"><QRCodeSVG value={pixPayload} size={160} /></div>
                  <div className="min-w-0 flex-1 space-y-3">
                    <div><span className="eyebrow block mb-1">{t.pixAmount}</span><span className="font-display text-2xl text-[#e77300]" data-testid="pix-amount">{totalFmt}</span></div>
                    <div>
                      <span className="eyebrow block mb-1">{t.pixKeyLabel}</span>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-white/90 bg-[#020815] border border-[#022a67] px-2 py-1.5 truncate" data-testid="pix-key">{PIX_KEY}</code>
                        <button onClick={()=>copyText(PIX_KEY)} className="text-[10px] font-mono-alt uppercase tracking-widest text-[#fdb429] hover:text-white flex items-center gap-1 shrink-0" data-testid="pix-copy-key"><Copy className="w-3 h-3"/>{t.pixCopy}</button>
                      </div>
                    </div>
                    <div>
                      <span className="eyebrow block mb-1">{t.pixCopyPaste}</span>
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-white/90 bg-[#020815] border border-[#022a67] px-2 py-1.5 truncate" data-testid="pix-payload">{pixPayload}</code>
                        <button onClick={()=>copyText(pixPayload)} className="text-[10px] font-mono-alt uppercase tracking-widest text-[#fdb429] hover:text-white flex items-center gap-1 shrink-0" data-testid="pix-copy-payload"><Copy className="w-3 h-3"/>{t.pixCopy}</button>
                      </div>
                    </div>
                    <p className="text-[11px] text-[#8ba3cc]">{t.pixAfter}</p>
                  </div>
                </div>
              </div>
            ); })()}
            {showConfirm && <div className="mt-5 p-4 border border-[#e77300]/50 bg-[#e77300]/5 text-sm" data-testid="confirm-msg">{t.confirmSent}</div>}
            {lang === "en" && <div className="mt-3 text-[11px] font-mono-alt text-[#8ba3cc] flex items-center gap-2"><Lock className="w-3 h-3"/> {t.payNotCfg}</div>}
          </div>
        </div>
      </section>
    </>
  );
}
